import React, { FC, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';

import { BackendRoute } from '../../config/index';
import { QueryKey } from '../../constants/index';
import { useAppQuery } from '../../hooks/index';
import { ApiService } from '../../services/index';
import { inviteStakeholderStore } from '../../storage/local/index';
import { Invitation } from '../../types/invitation.types';
import { PageRoute } from '../../types/page.types';
import { getSafeRedirectFromSearch, persistPostAuthRedirect } from '../../utils/auth-redirect.util';

const AcceptInvitation: FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  // Where the invitation was raised from, when something raised it on the way to somewhere: a data
  // room shared with somebody who is not in the company yet sends them here first, because this is
  // the page that knows whether the address has an account.
  const redirect = getSafeRedirectFromSearch(location.search);

  const {
    data: invitation,
    isPending,
    isError,
  } = useAppQuery({
    queryKey: [QueryKey.GET_INVITATION, id],
    queryFn: () =>
      ApiService.get<Invitation>({ endpoint: BackendRoute.INVITATIONS, routePath: [id || ''] }),
    enabled: !!id,
    defaultErrorHandling: false,
    retry: false,

    onError: () => {
      navigate('/error-page');
    },
  });

  useEffect(() => {
    if (isPending || !invitation || isError) return;

    inviteStakeholderStore.set({
      id,
      email: invitation.email,
      isRegistered: invitation.isUserRegistered,
      isAccepted: false,
    });

    // Signing up leaves the app to confirm the address, so the destination is stored as well as
    // navigated to; whichever way the person comes back, the auth flow clears it.
    if (redirect) persistPostAuthRedirect(redirect);

    if (!invitation.isUserRegistered) {
      navigate(PageRoute.REGISTER);

      return;
    }

    // The guard sends an unauthenticated visitor on to Log in with this address as the redirect,
    // and accepts the stored invitation once they are back — which is what turns their pending
    // data-room access into access they can use.
    navigate(redirect ?? PageRoute.HOME);
  }, [id, invitation, isPending, isError, navigate, redirect]);

  return <></>;
};

export default AcceptInvitation;
